import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, finalize, shareReplay } from 'rxjs/operators';
import {
    ActionEdit,
    ActionNote,
    ActionSnapshotDetailResult,
    ActionSnapshotGroupListItem,
    ActionSnapshotGroupListResult,
    CreateSnapshotResult,
    DeleteActionResult,
    DeleteActionSnapshotGroupResult,
    GetActionResult,
    GetActionsForJobsResult,
    InitActionResult,
    JobAction,
    JobActionSearchQuery,
    JobActionType,
    JobActionView,
    RecentActions,
    TimePeriod,
    UpdateActionResult,
} from './action.types';
import * as _ from 'lodash';
import { DatePipe } from '@angular/common';
import { JiraNameResult } from '../jobs';
import { PlannedBlockStatusPromptService } from './listing/planner/planned-block-status-prompt.service';

@Injectable({
    providedIn: 'root',
})
export class ActionService {
    private _jobActions: BehaviorSubject<JobAction[]> = new BehaviorSubject([]);
    private _jobActionsTotalResults: BehaviorSubject<number> =
        new BehaviorSubject(0);
    private _filterQuery: BehaviorSubject<JobActionSearchQuery> =
        new BehaviorSubject(null);
    private _view: BehaviorSubject<JobActionView> = new BehaviorSubject(
        (localStorage.getItem('action.view') as JobActionView) ||
            JobActionView.List
    );

    // Cache for action owners (all actions, no client filter) - persists across page navigations
    private _allActionOwnersCache: { userIds: string[]; groupIds: number[] } | null = null;
    private _allActionOwnersInFlight: Observable<{ userIds: string[]; groupIds: number[] }> | null = null;

    constructor(
        private _http: HttpClient,
        private _datePipe: DatePipe,
        @Inject('BASE_URL') private originUrl: string,
        private _plannedBlockStatusPrompt: PlannedBlockStatusPromptService
    ) {}

    get jobActions$(): Observable<JobAction[]> {
        return this._jobActions.asObservable();
    }

    set jobActions(value: JobAction[]) {
        this._jobActions.next(value);
    }

    set view(value: JobActionView) {
        localStorage.setItem('action.view', value);
        this._view.next(value);
    }

    get view(): JobActionView {
        return this._view.value;
    }

    get view$(): Observable<JobActionView> {
        return this._view.asObservable();
    }

    get filterQuery$(): Observable<JobActionSearchQuery> {
        return this._filterQuery.asObservable();
    }

    get jobActionsTotalResults$(): Observable<number> {
        return this._jobActionsTotalResults.asObservable();
    }

    get(actionId: number): Observable<GetActionResult> {
        return this._http
            .get<GetActionResult>(`${this.originUrl}api/action/${actionId}`)
            .pipe(map((r) => new GetActionResult(r)));
    }

    getAll(query?: JobActionSearchQuery): Observable<JobAction[]> {
        if (this.view === JobActionView.Board) {
            query.resultsPerPage = -1;
            query.sortBy = 'priority';
            query.sortDir = 'desc';
        }
        return this._http.get<JobAction[]>(this._buildSearchQuery(query)).pipe(
            map((response: any) => {
                if (response.success) {
                    // let jobActions: any = query.isPaginating
                    //     ? this._jobActions.value
                    //     : [];
                    // response.jobActions.forEach((data) =>
                    //     jobActions.push(this._mapJobAction(data))
                    // );

                    // Cache for future use
                    this._jobActions.next(response.jobActions);
                    this._jobActionsTotalResults.next(response.totalResults);

                    return response.jobActions;
                }

                throw new Error(response.error);
            })
        );
    }

    // Same GET api/action search as getAll(), but doesn't touch the shared
    // _jobActions / _jobActionsTotalResults / _filterQuery state that the
    // rest of the Actions page (list/board rendering, snapshot capture)
    // reads from — getAll() and _buildSearchQuery() both write into those.
    // For callers that need their own one-off search result without
    // disturbing whatever the main list/board is currently showing, e.g.
    // the Planner pull-out's "actions scheduled for me" list.
    searchActions(query: JobActionSearchQuery): Observable<JobAction[]> {
        const params: string[] = [`page=${query.page ?? 1}`];
        if (query.clientId) params.push(`clientId=${query.clientId}`);
        if (query.jobId) params.push(`jobId=${query.jobId}`);
        if (query.owner) params.push(`owner=${encodeURIComponent(query.owner)}`);
        if (query.type) params.push(`type=${query.type}`);
        if (query.jobTypeIds?.length) {
            query.jobTypeIds.forEach((id) => params.push(`jobTypeIds=${id}`));
        }
        if (query.searchTerm) params.push(`searchTerm=${encodeURIComponent(query.searchTerm)}`);
        if (query.sortBy) params.push(`sortBy=${query.sortBy}&sortDir=${query.sortDir || 'asc'}`);
        if (query.status) params.push(`status=${query.status}`);
        if (query.timePeriod) params.push(`timePeriod=${query.timePeriod}`);
        if (query.resultsPerPage) params.push(`resultsPerPage=${query.resultsPerPage}`);

        return this._http
            .get<any>(`${this.originUrl}api/action?${params.join('&')}`)
            .pipe(
                map((response) => {
                    if (response.success) return response.jobActions;
                    throw new Error(response.error);
                })
            );
    }

    getNotes(jobActionId: number): Observable<ActionNote[]> {
        return this._http
            .get<ActionNote[]>(
                `${this.originUrl}api/action/${jobActionId}/notes`
            )
            .pipe(
                map((response: any) => {
                    if (response.success) {
                        return response.notes;
                    }

                    throw new Error(response.error);
                })
            );
    }

    create(action: JobAction): Observable<JobAction> {
        let body = JSON.stringify(action);

        return this._http
            .post<UpdateActionResult>(`${this.originUrl}api/action`, body)
            .pipe(
                map((result) => {
                    if (!result.success) {
                        //return Observable.throw(result.errorMessage);
                        throw new Error(result.error);
                    }

                    let currentJobActions = this._jobActions.getValue();
                    currentJobActions.push(result.jobAction);
                    this._jobActions.next(currentJobActions);

                    return result.jobAction;
                })
            );
    }

    update(action: JobAction): Observable<JobAction> {
        return this._http
            .patch<UpdateActionResult>(
                `${this.originUrl}api/action`,
                JSON.stringify(action)
            )
            .pipe(
                map((r) => {
                    const result = new UpdateActionResult(r);
                    if (!result.success) {
                        throw new Error(result.error);
                    }

                    // Update the action locally
                    let currentJobActions = this._jobActions.getValue();
                    currentJobActions = currentJobActions.map((a) => {
                        if (a.jobActionId === result.jobAction.jobActionId) {
                            // The single-action PATCH response doesn't carry the
                            // section-boundary flags computed by the bulk listing
                            // endpoint, so keep the existing ones to avoid losing
                            // the Unscheduled section divider/header on edit.
                            result.jobAction.splitByStatus = a.splitByStatus;
                            result.jobAction.showTitle = a.showTitle;
                            a = result.jobAction;
                        }

                        return a;
                    });

                    this._jobActions.next(currentJobActions);

                    // Completed/Archived with planned blocks after today →
                    // offers to remove them (Planner spec). Runs after the
                    // save has succeeded and never affects it.
                    this._plannedBlockStatusPrompt.onActionSaved(result.jobAction);

                    return result.jobAction;
                })
            );
    }

    reload() {
        this._jobActions.next([...this._jobActions.getValue()]);
    }

    // removePlannedFrom: also remove the action's planned blocks from this
    // moment on (the user's answer to "remove its future planned blocks?" —
    // see PlannedBlockStatusPromptService.askBeforeDelete). Its past and
    // today's blocks are always kept by the server.
    delete(jobActionId: number, removePlannedFrom?: Date | null): Observable<boolean> {
        const query = removePlannedFrom
            ? `?removePlannedFrom=${encodeURIComponent(removePlannedFrom.toISOString())}`
            : '';
        return this._http
            .delete<DeleteActionResult>(
                `${this.originUrl}api/action/${jobActionId}${query}`
            )
            .pipe(
                map((r) => {
                    const result = new DeleteActionResult(r);
                    if (!result.success) {
                        throw new Error(result.error);
                    }

                    // Remove from cache
                    let currentJobActions = this._jobActions.getValue();
                    currentJobActions = currentJobActions.filter(
                        (a) => a.jobActionId !== jobActionId
                    );

                    this._jobActions.next(currentJobActions);

                    return true;
                })
            );
    }

    getMarginDataForJobs(jobIds: number[]): Observable<any[]> {
        const params = jobIds.map(id => `jobid=${id}`).join('&');
        const url = `${this.originUrl}api/job/GetJobValueGroupTotals?${params}`;
        return this._http.get<any[]>(url);
    }

    // Bulk lookup of a single action type's actions across many jobs - e.g. the
    // Invoice-type actions behind a page of Finance > Month End rows. Backed by
    // api/action/GetActionsForJobs, which already sorts by DeadlineDate, then
    // ScheduleDate, then JobActionId, so callers don't need to re-sort.
    getActionsForJobs(jobIds: number[], type: JobActionType): Observable<JobAction[]> {
        if (!jobIds.length) {
            return of([]);
        }

        const params = jobIds.map(id => `jobId=${id}`).join('&');
        const url = `${this.originUrl}api/action/GetActionsForJobs?${params}&type=${type}`;

        return this._http.get<GetActionsForJobsResult>(url).pipe(
            map((r) => {
                const result = new GetActionsForJobsResult(r);
                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.jobActions;
            })
        );
    }

    getActionCount(jobId: number): Observable<number> {
        // Fetch the count of actions for a specific job
        return this._http
          .get<{
              success: boolean;
              jobActions: any[];
              totalResults: number;
          }>(
              `${this.originUrl}api/action?jobId=${jobId}&page=1&resultsPerPage=1`
          )
          .pipe(
              map(resp => {
                  if (!resp.success) throw new Error('Failed to fetch count');
                  return resp.totalResults;
              })
          );
    }

    fetchAllForJob(jobId: number): Observable<JobAction[]> {
        const url = `${this.originUrl}api/action?page=1&jobId=${jobId}&resultsPerPage=-1`;
        interface Resp { success: boolean; jobActions: JobAction[]; error?: string }
        return this._http.get<Resp>(url).pipe(
            map(r => {
                if (!r.success) throw new Error(r.error || 'Error loading actions');
                return r.jobActions;
            })
        );
    }

    /**
     * Fetches actions owned by a specific user without updating shared state.
     * Used by timer-header to show only the user's jobs.
     */
    fetchActionsForOwner(userId: string): Observable<JobAction[]> {
        const url = `${this.originUrl}api/action?page=1&owner=${userId}&resultsPerPage=-1`;
        interface Resp { success: boolean; jobActions: JobAction[]; error?: string }
        return this._http.get<Resp>(url).pipe(
            map(r => {
                if (!r.success) throw new Error(r.error || 'Error loading actions');
                return r.jobActions;
            })
        );
    }

    getRecentActionsForUser(
        userid: string,
        period: TimePeriod
    ): Observable<RecentActions> {
        return this._http.get<RecentActions>(
            `${this.originUrl}api/action/user/${userid}/${period}`
        );
    }

    getRecentActionsForGroup(
        groupid: number,
        period: TimePeriod
    ): Observable<RecentActions> {
        return this._http.get<RecentActions>(
            `${this.originUrl}api/action/group/${groupid}/${period}`
        );
    }

    getRecentActionsForUserAfterLastUpdate(
        userid: string,
        period: TimePeriod,
        lastupdated: string
    ): Observable<RecentActions> {
        var params = new URLSearchParams();
        params.set('mostrecent', lastupdated);
        return this._http.get<RecentActions>(
            `${
                this.originUrl
            }api/action/user/${userid}/${period}?mostrecent=${encodeURIComponent(
                lastupdated
            )}`
        );
    }

    getRecentActionsForGroupAfterLastUpdate(
        groupid: number,
        period: TimePeriod,
        lastupdated: string
    ): Observable<RecentActions> {
        var params = new URLSearchParams();
        params.set('mostrecent', lastupdated);
        return this._http.get<RecentActions>(
            `${
                this.originUrl
            }api/action/group/${groupid}?mostrecent=${encodeURIComponent(
                lastupdated
            )}`
        );
    }

    submitNoteForAction(note: ActionNote): Observable<ActionNote[]> {
        let body = JSON.stringify(note);

        return this._http
            .post<ActionNote[]>(
                `${this.originUrl}api/action/${note.jobActionId}/notes`,
                body
            )
            .pipe(
                map((response: any) => {
                    if (response.success) {
                        return response.notes;
                    }

                    throw new Error(response.error);
                })
            );
    }

    deleteNoteForAction(jobActionId: number, noteId: number) {
        return this._http
            .delete<ActionNote[]>(
                `${this.originUrl}api/action/${jobActionId}/notes/${noteId}`
            )
            .pipe(
                map((response: any) => {
                    if (response.success) {
                        return response.notes;
                    }

                    throw new Error(response.error);
                })
            );
    }

    editNoteForAction(jobActionId: number, noteId: number, text: string) {
        let body = JSON.stringify({ text });

        return this._http
            .put<ActionNote[]>(
                `${this.originUrl}api/action/${jobActionId}/notes/${noteId}`,
                body
            )
            .pipe(
                map((response: any) => {
                    if (response.success) {
                        return response.notes;
                    }

                    throw new Error(response.error);
                })
            );
    }

    actionFieldStartEdit(
        jobActionId: number,
        userId: string,
        shortName: string,
        propertyName: string
    ) {
        let body = JSON.stringify({
            userid: userId,
            name: shortName,
            prop: propertyName,
            jobActionId: jobActionId,
        });

        this._http
            .post<ActionEdit[]>(`${this.originUrl}api/action/StartEdit`, body)
            .subscribe((r) => {
                console.log(r);
            });
    }

    actionFieldStopEdit(
        jobActionId: number,
        userId: string,
        shortName: string,
        propertyName: string
    ) {
        let body = JSON.stringify({
            userid: userId,
            name: shortName,
            prop: propertyName,
            jobActionId: jobActionId,
        });

        this._http
            .post<ActionEdit[]>(`${this.originUrl}api/action/StopEdit`, body)
            .subscribe((r) => {
                console.log(r);
            });
    }

    initRecurAction(jobId: number): Observable<InitActionResult> {
        let body = JSON.stringify({
            jobId: jobId,
            parentActionId: 0,
        });

        return this._http
            .post<InitActionResult>(
                `${this.originUrl}api/action/InitRecurAction`,
                body
            )
            .pipe(map((r) => new InitActionResult(r)));
    }

    /**
     * Fetches unique owner user IDs and group IDs from all actions.
     * Optionally filters by clientId.
     * Results for "all owners" (no client filter) are cached in the service.
     */
    getActionOwners(clientId?: number): Observable<{ userIds: string[]; groupIds: number[] }> {
        const isAllOwners = !clientId;

        // Return cached result immediately if available (for "all owners" query)
        if (isAllOwners && this._allActionOwnersCache) {
            return of(this._allActionOwnersCache);
        }

        // Return in-flight request if one exists (for "all owners" query)
        if (isAllOwners && this._allActionOwnersInFlight) {
            return this._allActionOwnersInFlight;
        }

        // Use the dedicated endpoint
        let url = `${this.originUrl}api/action/assignedids`;
        if (clientId) {
            url += `/${clientId}`;
        }

        const request$ = this._http.get<{ userIds: string[]; groupIds: number[] }>(url).pipe(
            map((response) => {
                return {
                    userIds: response.userIds || [],
                    groupIds: response.groupIds || [],
                };
            }),
            tap((result) => {
                // Cache "all owners" result
                if (isAllOwners) {
                    this._allActionOwnersCache = result;
                    this._allActionOwnersInFlight = null;
                }
            }),
            finalize(() => {
                if (isAllOwners) {
                    this._allActionOwnersInFlight = null;
                }
            }),
            shareReplay(1)
        );

        // Track in-flight request for "all owners" query
        if (isAllOwners) {
            this._allActionOwnersInFlight = request$;
        }

        return request$;
    }

    /**
     * Returns cached action owners if available, or null if not yet loaded.
     * Use this for instant access without triggering a fetch.
     */
    getCachedActionOwners(): { userIds: string[]; groupIds: number[] } | null {
        return this._allActionOwnersCache;
    }

    reset(): void {
        this._jobActions.next([]);
        this._jobActionsTotalResults.next(0);
    }

    clearFilterQuery(): void {
        this._filterQuery.next(null);
    }

    /** Shares a new search query (filterQuery$) without loading any
     *  actions — for the Planner view, which loads its own data and never
     *  shows the list/board results that getAll() would fetch. */
    publishFilterQuery(query: JobActionSearchQuery): void {
        this._filterQuery.next(query);
    }

    getJiraName(url: string): Observable<JiraNameResult> {
        // make an http call to api/Job/GetJiraName with a query parameter of url
        return this._http.get<JiraNameResult>(`${this.originUrl}api/Job/GetJiraName?url=${url}`);

    }

    private _buildSearchQuery(searchQuery?: JobActionSearchQuery): string {
        let baseUrl = `${this.originUrl}api/action`;
        if (!searchQuery) return `${baseUrl}?page=1`;

        let query = `?page=${searchQuery.page}`;

        if (searchQuery.clientId) {
            query += `&clientId=${searchQuery.clientId}`;
        }

        if (searchQuery.jobId) {
            query += `&jobId=${searchQuery.jobId}`;
        }

        if (searchQuery.owner) {
            query += `&owner=${searchQuery.owner}`;
        }

        if (searchQuery.type) {
            query += `&type=${searchQuery.type}`;
        }

        if (searchQuery.jobTypeIds?.length) {
            searchQuery.jobTypeIds.forEach((id) => {
                query += `&jobTypeIds=${id}`;
            });
        }

        if (searchQuery.searchTerm) {
            query += `&searchTerm=${encodeURIComponent(
                searchQuery.searchTerm
            )}`;
        }

        if (searchQuery.sortBy) {
            let direction = searchQuery.sortDir || 'asc';
            query += `&sortBy=${searchQuery.sortBy}&sortDir=${direction}`;
        }

        if (searchQuery.status) {
            query += `&status=${searchQuery.status}`;
        }

        if (searchQuery.timePeriod) {
            query += `&timePeriod=${searchQuery.timePeriod}`;
        }

        if (searchQuery.resultsPerPage) {
            query += `&resultsPerPage=${searchQuery.resultsPerPage}`;
        }

        if (searchQuery.plannedOnly) {
            query += `&plannedOnly=true`;
        }

        //if (!_.isEqual(this._filterQuery.value, searchQuery)) {
        this._filterQuery.next(searchQuery);
        //}

        return `${baseUrl}${query}`;
    }

    // Captures a snapshot of the actions currently matching the given filter
    // (used by the PM-only "Create Snapshot" button on /actions/listing when
    // "Today's deadlines" is selected). The backend re-runs the same filtering
    // logic used by the listing itself, ignoring pagination, so every matching
    // action is captured.
    createSnapshot(
        query: JobActionSearchQuery
    ): Observable<CreateSnapshotResult> {
        const body = JSON.stringify(query);

        return this._http
            .post<CreateSnapshotResult>(
                `${this.originUrl}api/action/CreateSnapshot`,
                body
            )
            .pipe(
                map((r) => {
                    const result = new CreateSnapshotResult(r);
                    if (!result.success) {
                        throw new Error(result.error);
                    }

                    return result;
                })
            );
    }

    // Lists every captured snapshot group for the /report/actionsnapshots page.
    // Viewable by any authenticated user (unlike createSnapshot/delete, no PM
    // check on the frontend - the backend only restricts the delete call).
    getActionSnapshotGroups(): Observable<ActionSnapshotGroupListItem[]> {
        return this._http
            .get<ActionSnapshotGroupListResult>(
                `${this.originUrl}api/action/GetActionSnapshotGroups`
            )
            .pipe(
                map((r) => {
                    const result = new ActionSnapshotGroupListResult(r);
                    if (!result.success) {
                        throw new Error(result.error);
                    }

                    return result.groups;
                })
            );
    }

    // Deletes a captured snapshot group. PM-only server-side (see IsPM() in
    // ActionController.DeleteActionSnapshotGroup).
    deleteActionSnapshotGroup(actionSnapshotGroupId: number): Observable<boolean> {
        return this._http
            .delete<DeleteActionSnapshotGroupResult>(
                `${this.originUrl}api/action/DeleteActionSnapshotGroup/${actionSnapshotGroupId}`
            )
            .pipe(
                map((r) => {
                    const result = new DeleteActionSnapshotGroupResult(r);
                    if (!result.success) {
                        throw new Error(result.error);
                    }

                    return true;
                })
            );
    }
    // Fetches full detail for one captured snapshot group, for the
    // /report/actionsnapshot/:id page - every originally-captured action
    // (current live data plus the values captured at snapshot time) plus any
    // "new" actions matching the snapshot's owners with a deadline of today.
    // Viewable by any authenticated user.
    getActionSnapshotGroupDetail(actionSnapshotGroupId: number): Observable<ActionSnapshotDetailResult> {
        return this._http
            .get<ActionSnapshotDetailResult>(
                `${this.originUrl}api/action/GetActionSnapshotGroupDetail/${actionSnapshotGroupId}`
            )
            .pipe(
                map((r) => {
                    const result = new ActionSnapshotDetailResult(r);
                    if (!result.success) {
                        throw new Error(result.error);
                    }

                    return result;
                })
            );
    }
}
